import { useEffect, useState } from "react";
import { Plus, Trash2, FolderTree, BookOpen, Layers } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { toast } from "sonner";

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [catDialog, setCatDialog] = useState(false);
  const [topicDialog, setTopicDialog] = useState(false);
  const [blockDialog, setBlockDialog] = useState(false);
  const [newCat, setNewCat] = useState({ id: "", name: "", description: "" });
  const [newTopic, setNewTopic] = useState({ id: "", name: "", block_id: "" });
  const [newBlock, setNewBlock] = useState({ id: "", name: "", order: 0 });

  const load = async () => {
    const { data } = await api.get("/categories");
    setCats(data);
    if (!activeCat && data.length) setActiveCat(data[0].id);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const cat = cats.find((c) => c.id === activeCat);

  const createCategory = async () => {
    try {
      await api.post("/admin/categories", { ...newCat, icon: "BookOpen" });
      toast.success("Categoría creada");
      setCatDialog(false);
      setNewCat({ id: "", name: "", description: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const deleteCategory = async (id) => {
    if (!window.confirm("¿Eliminar categoría y todas sus preguntas?")) return;
    try {
      await api.delete(`/admin/categories/${id}`);
      toast.success("Categoría eliminada");
      setActiveCat(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const addTopic = async () => {
    try {
      await api.post(`/admin/categories/${activeCat}/topics`, {
        ...newTopic,
        block_id: newTopic.block_id || null,
      });
      toast.success("Tema añadido");
      setTopicDialog(false);
      setNewTopic({ id: "", name: "", block_id: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const deleteTopic = async (tid) => {
    if (!window.confirm("¿Eliminar tema?")) return;
    await api.delete(`/admin/categories/${activeCat}/topics/${tid}`);
    toast.success("Tema eliminado");
    load();
  };
  const addBlock = async () => {
    try {
      await api.post(`/admin/categories/${activeCat}/blocks`, newBlock);
      toast.success("Bloque añadido");
      setBlockDialog(false);
      setNewBlock({ id: "", name: "", order: 0 });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const deleteBlock = async (bid) => {
    if (!window.confirm("¿Eliminar bloque?")) return;
    await api.delete(`/admin/categories/${activeCat}/blocks/${bid}`);
    toast.success("Bloque eliminado");
    load();
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-gc-gold mb-2">Categorías</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Estructura de contenidos</h1>
        </div>
        <Button onClick={() => setCatDialog(true)} className="bg-gc-gold hover:bg-gc-gold-light text-white" data-testid="create-category-button">
          <Plus className="h-4 w-4 mr-2" /> Nueva categoría
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Sidebar of categories */}
        <div className="bg-white border border-slate-200 rounded-sm" data-testid="categories-sidebar">
          <div className="px-4 py-3 border-b border-slate-200 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 flex items-center gap-2">
            <FolderTree className="h-3.5 w-3.5" /> Categorías
          </div>
          <div>
            {cats.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.id)}
                className={`flex items-center justify-between w-full px-4 py-3 text-sm border-b border-slate-100 last:border-0 transition-colors ${
                  activeCat === c.id ? "bg-gc-green/5 text-gc-green font-semibold" : "text-slate-700 hover:bg-slate-50"
                }`}
                data-testid={`cat-tab-${c.id}`}
              >
                <span className="truncate">{c.name}</span>
                <span className="text-xs font-mono text-slate-400">{c.question_count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Detail */}
        {cat && (
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-sm p-5 flex items-start justify-between gap-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-gc-gold">{cat.id}</div>
                <h2 className="font-display text-2xl font-bold text-slate-900 mt-1">{cat.name}</h2>
                <p className="text-sm text-slate-600 mt-1.5">{cat.description}</p>
              </div>
              <Button variant="outline" onClick={() => deleteCategory(cat.id)} className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-700">
                <Trash2 className="h-4 w-4 mr-1.5" /> Eliminar
              </Button>
            </div>

            <Tabs defaultValue="topics">
              <TabsList>
                <TabsTrigger value="topics" data-testid="tab-topics"><BookOpen className="h-3.5 w-3.5 mr-1.5" />Temas</TabsTrigger>
                <TabsTrigger value="blocks" data-testid="tab-blocks"><Layers className="h-3.5 w-3.5 mr-1.5" />Bloques</TabsTrigger>
              </TabsList>

              <TabsContent value="topics" className="mt-4">
                <div className="bg-white border border-slate-200 rounded-sm">
                  <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-900">{cat.topics?.length || 0} temas</div>
                    <Button size="sm" onClick={() => setTopicDialog(true)} className="bg-gc-green hover:bg-gc-green-light text-white" data-testid="add-topic-button">
                      <Plus className="h-4 w-4 mr-1.5" /> Añadir tema
                    </Button>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {(cat.topics || []).map((t) => (
                      <div key={t.id} className="px-4 py-3 flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium text-slate-900">{t.name}</div>
                          <div className="text-xs text-slate-500 font-mono">{t.id} {t.block_id ? `· ${t.block_id}` : ""}</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-mono text-slate-400">{t.question_count || 0} preguntas</span>
                          <Button variant="ghost" size="sm" onClick={() => deleteTopic(t.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="blocks" className="mt-4">
                <div className="bg-white border border-slate-200 rounded-sm">
                  <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-900">{cat.blocks?.length || 0} bloques</div>
                    <Button size="sm" onClick={() => setBlockDialog(true)} className="bg-gc-green hover:bg-gc-green-light text-white" data-testid="add-block-button">
                      <Plus className="h-4 w-4 mr-1.5" /> Añadir bloque
                    </Button>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {(cat.blocks || []).map((b) => (
                      <div key={b.id} className="px-4 py-3 flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium text-slate-900">{b.name}</div>
                          <div className="text-xs text-slate-500 font-mono">{b.id} · orden {b.order ?? 0}</div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => deleteBlock(b.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>

      {/* New Category */}
      <Dialog open={catDialog} onOpenChange={setCatDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva categoría</DialogTitle>
            <DialogDescription>Crea una categoría de tests.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">ID (sin espacios, en minúsculas)</Label>
              <Input value={newCat.id} onChange={(e) => setNewCat({ ...newCat, id: e.target.value.toLowerCase().replace(/\s/g, "-") })} className="mt-1" data-testid="new-cat-id" />
            </div>
            <div><Label className="text-xs">Nombre</Label>
              <Input value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} className="mt-1" data-testid="new-cat-name" />
            </div>
            <div><Label className="text-xs">Descripción</Label>
              <Input value={newCat.description} onChange={(e) => setNewCat({ ...newCat, description: e.target.value })} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDialog(false)} className="border-slate-300">Cancelar</Button>
            <Button onClick={createCategory} className="bg-gc-gold hover:bg-gc-gold-light text-white" data-testid="save-cat">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Topic */}
      <Dialog open={topicDialog} onOpenChange={setTopicDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Añadir tema a {cat?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">ID</Label>
              <Input value={newTopic.id} onChange={(e) => setNewTopic({ ...newTopic, id: e.target.value.toLowerCase().replace(/\s/g, "-") })} className="mt-1" />
            </div>
            <div><Label className="text-xs">Nombre</Label>
              <Input value={newTopic.name} onChange={(e) => setNewTopic({ ...newTopic, name: e.target.value })} className="mt-1" />
            </div>
            {(cat?.blocks || []).length > 0 && (
              <div><Label className="text-xs">Bloque (opcional)</Label>
                <select className="w-full mt-1 h-10 border border-slate-200 rounded-sm px-2 text-sm" value={newTopic.block_id} onChange={(e) => setNewTopic({ ...newTopic, block_id: e.target.value })}>
                  <option value="">(sin bloque)</option>
                  {cat.blocks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTopicDialog(false)} className="border-slate-300">Cancelar</Button>
            <Button onClick={addTopic} className="bg-gc-gold hover:bg-gc-gold-light text-white">Añadir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Block */}
      <Dialog open={blockDialog} onOpenChange={setBlockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Añadir bloque a {cat?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">ID</Label>
              <Input value={newBlock.id} onChange={(e) => setNewBlock({ ...newBlock, id: e.target.value.toLowerCase().replace(/\s/g, "-") })} className="mt-1" />
            </div>
            <div><Label className="text-xs">Nombre</Label>
              <Input value={newBlock.name} onChange={(e) => setNewBlock({ ...newBlock, name: e.target.value })} className="mt-1" />
            </div>
            <div><Label className="text-xs">Orden</Label>
              <Input type="number" value={newBlock.order} onChange={(e) => setNewBlock({ ...newBlock, order: parseInt(e.target.value || "0") })} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockDialog(false)} className="border-slate-300">Cancelar</Button>
            <Button onClick={addBlock} className="bg-gc-gold hover:bg-gc-gold-light text-white">Añadir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
